import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const { user, signOut, isAdmin } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsAccountMenuOpen(false);
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* ロゴ */}
            <Link to="/" className="flex items-center space-x-2 cursor-pointer group">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                <i className="ri-book-open-line text-white text-lg"></i>
              </div>
              <span className="text-xl font-bold text-gray-900 relative">
                キョウコレ
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#800019] group-hover:w-full transition-all duration-300"></span>
              </span>
            </Link>

            {/* デスクトップナビゲーション */}
            <nav className="hidden md:flex items-center space-x-1">
              <Link
                to="/materials"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname.startsWith('/materials')
                    ? 'bg-teal-50 text-teal-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-book-2-line mr-1"></i>
                単元で探す
              </Link>
              <Link
                to="/sites"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname.startsWith('/sites')
                    ? 'bg-teal-50 text-teal-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-global-line mr-1"></i>
                サイトで探す
              </Link>
              <Link
                to="/recipes"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname.startsWith('/recipes')
                    ? 'bg-teal-50 text-teal-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-lightbulb-line mr-1"></i>
                活用レシピ
              </Link>
              <Link
                to="/features"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname.startsWith('/features')
                    ? 'bg-teal-50 text-teal-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-star-line mr-1"></i>
                特集
              </Link>
              <Link
                to="/questions"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname.startsWith('/questions')
                    ? 'bg-teal-50 text-teal-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-question-answer-line mr-1"></i>
                学習相談
              </Link>
              <Link
                to="/voices"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname.startsWith('/voices')
                    ? 'bg-teal-50 text-teal-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className="ri-chat-voice-line mr-1"></i>
                居場所の声
              </Link>
            </nav>

            {/* ユーザーメニュー */}
            <div className="flex items-center space-x-4">
              {user ? (
                <div className="relative" ref={accountMenuRef}>
                  <button
                    onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-teal-600"></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 hidden lg:block">
                      マイページ
                    </span>
                    <i className={`ri-arrow-down-s-line text-gray-500 transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {isAccountMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                      <div className="px-4 py-2 border-b border-gray-100 relative">
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-0.5 h-8 bg-[#800019]"></div>
                        <p className="text-xs text-gray-500 pl-3">ログイン中</p>
                        <p className="text-sm font-medium text-gray-900 truncate pl-3">
                          {user.email}
                        </p>
                      </div>
                      <Link
                        to="/account/helpful-recipes"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setIsAccountMenuOpen(false)}
                      >
                        <i className="ri-heart-line mr-2"></i>
                        役に立ったレシピ
                      </Link>
                      <Link
                        to="/account/my-recipes"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setIsAccountMenuOpen(false)}
                      >
                        <i className="ri-file-list-line mr-2"></i>
                        投稿したレシピ
                      </Link>
                      <Link
                        to="/account/my-materials"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setIsAccountMenuOpen(false)}
                      >
                        <i className="ri-book-marked-line mr-2"></i>
                        マイ教材棚
                      </Link>
                      <Link
                        to="/account/my-reviews"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setIsAccountMenuOpen(false)}
                      >
                        <i className="ri-star-line mr-2"></i>
                        自分のレビュー
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                          onClick={() => setIsAccountMenuOpen(false)}
                        >
                          <i className="ri-settings-line mr-2"></i>
                          管理画面
                        </Link>
                      )}
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                      >
                        <i className="ri-logout-box-line mr-2"></i>
                        ログアウト
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap relative overflow-hidden group"
                >
                  <span className="relative z-10">
                    <i className="ri-login-box-line mr-1"></i>
                    ログイン
                  </span>
                  <span className="absolute inset-0 bg-[#800019] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                </Link>
              )}

              {/* モバイルメニューボタン */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
              >
                <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
              </button>
            </div>
          </div>

          {/* モバイルメニュー */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="space-y-1">
                <Link
                  to="/materials"
                  className={`block px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${
                    location.pathname.startsWith('/materials')
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-book-2-line mr-2"></i>
                  単元で探す
                </Link>
                <Link
                  to="/sites"
                  className={`block px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${
                    location.pathname.startsWith('/sites')
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-global-line mr-2"></i>
                  サイトで探す
                </Link>
                <Link
                  to="/recipes"
                  className={`block px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${
                    location.pathname.startsWith('/recipes')
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-lightbulb-line mr-2"></i>
                  活用レシピ
                </Link>
                <Link
                  to="/features"
                  className={`block px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${
                    location.pathname.startsWith('/features')
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-star-line mr-2"></i>
                  特集
                </Link>
                <Link
                  to="/questions"
                  className={`block px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${
                    location.pathname.startsWith('/questions')
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-question-answer-line mr-2"></i>
                  学習相談
                </Link>
                <Link
                  to="/voices"
                  className={`block px-3 py-2 rounded-md text-sm font-medium cursor-pointer ${
                    location.pathname.startsWith('/voices')
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-chat-voice-line mr-2"></i>
                  居場所の声
                </Link>

                {user && (
                  <>
                    <div className="border-t border-gray-200 my-2"></div>
                    <Link
                      to="/account/helpful-recipes"
                      className="block px-3 py-2 text-gray-700 hover:text-teal-600 hover:bg-gray-50 rounded-md cursor-pointer"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="ri-heart-line mr-2"></i>
                      役に立ったレシピ
                    </Link>
                    <Link
                      to="/account/my-recipes"
                      className="block px-3 py-2 text-gray-700 hover:text-teal-600 hover:bg-gray-50 rounded-md cursor-pointer"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="ri-file-list-line mr-2"></i>
                      投稿したレシピ
                    </Link>
                    <Link
                      to="/account/my-materials"
                      className="block px-3 py-2 text-gray-700 hover:text-teal-600 hover:bg-gray-50 rounded-md cursor-pointer"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="ri-book-marked-line mr-2"></i>
                      マイ教材棚
                    </Link>
                    <Link
                      to="/account/my-reviews"
                      className="block px-3 py-2 text-gray-700 hover:text-teal-600 hover:bg-gray-50 rounded-md cursor-pointer"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="ri-star-line mr-2"></i>
                      自分のレビュー
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="block px-3 py-2 text-gray-700 hover:text-teal-600 hover:bg-gray-50 rounded-md cursor-pointer"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <i className="ri-settings-line mr-2"></i>
                        管理画面
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 rounded-md cursor-pointer"
                    >
                      <i className="ri-logout-box-line mr-2"></i>
                      ログアウト
                    </button>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="flex-1">
        {children}
      </main>

      {/* フッター */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* ロゴとキャッチコピー */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4 cursor-pointer group">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                  <i className="ri-book-open-line text-white text-lg"></i>
                </div>
                <span className="text-xl font-bold text-gray-900 relative">
                  キョウコレ
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#800019] group-hover:w-full transition-all duration-300"></span>
                </span>
              </Link>
              <p className="text-sm text-gray-600 mb-4 pl-1 border-l-2 border-[#800019]">
                <span className="pl-2">不登校・ホームスクーリングの学習を支援する教材情報プラットフォーム</span>
              </p>
            </div>

            {/* リンク */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 relative inline-block">
                コンテンツ
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#800019]"></span>
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/materials" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    単元で探す
                  </Link>
                </li>
                <li>
                  <Link to="/sites" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    サイトで探す
                  </Link>
                </li>
                <li>
                  <Link to="/recipes" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    活用レシピ
                  </Link>
                </li>
                <li>
                  <Link to="/features" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    特集
                  </Link>
                </li>
                <li>
                  <Link to="/questions" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    学習相談
                  </Link>
                </li>
                <li>
                  <Link to="/voices" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    居場所の声
                  </Link>
                </li>
              </ul>
            </div>

            {/* サイト情報 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 relative inline-block">
                サイト情報
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#800019]"></span>
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    このサイトについて
                  </Link>
                </li>
                <li>
                  <Link to="/feedback" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    ご意見・ご要望
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    利用規約
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-sm text-gray-600 hover:text-teal-600 cursor-pointer">
                    プライバシーポリシー
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* コピーライト */}
          <div className="mt-8 pt-8 border-t border-gray-200 relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-[#800019]"></div>
            <p className="text-sm text-gray-500 text-center">
              © 2024 キョウコレ. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
